import React, { useEffect } from 'react';
import styled from '@emotion/styled';

import { Box, Paper as MuiPaper, Stack, Typography, TypographyProps } from '@mui/material';
import { spacing } from '@mui/system';
import { CustomerProfileResponse, ServiceLocationDto } from '../../apis/customerApi';
import { useCustomerStore, useSessionStore } from '../../stores';
import Loader from '../../components/common/Loader';
import PageHeader from '../../components/common/PageHeader';

const Paper = styled(MuiPaper)(spacing);

const TextLine: React.FC<TypographyProps> = (props: TypographyProps) => {
  return <Typography variant="subtitle1" {...props}></Typography>;
};

const MainHeading: React.FC<TypographyProps> = (props: TypographyProps) => {
  return <Typography variant="h3" pl={1} pt={6} pb={0} {...props}></Typography>;
};

const Heading: React.FC<TypographyProps> = (props: TypographyProps) => {
  return <Typography variant="h4" pl={1} pt={6} pb={0} {...props}></Typography>;
};

type CustomerProps = {
  customer: CustomerProfileResponse;
};

type ServiceLocationProps = {
  serviceLocations: ServiceLocationDto[];
};

const BillingInfo: React.FC<CustomerProps> = (props) => {
  const { customer } = props;
  return (
    <Box pt={2}>
      <Paper p={3} sx={{ border: '1px solid', borderColor: (theme) => theme.palette.divider }}>
        <TextLine>{customer.billingAddress}</TextLine>
        <TextLine>
          {customer.billingCity}, {customer.billingState} {customer.billingZip}
        </TextLine>
      </Paper>
    </Box>
  );
};

const ServiceLocations: React.FC<ServiceLocationProps> = (props) => {
  const { serviceLocations } = props;
  return serviceLocations.map((serviceLocation, index) => (
    <Box pt={2}>
      <Paper px={3} py={3} key={`ServiceLocation_${index}`} sx={{ border: '1px solid', borderColor: (theme) => theme.palette.divider }}>
        <TextLine>{serviceLocation.address}</TextLine>
        <TextLine>
          {serviceLocation.city}, {serviceLocation.state} {serviceLocation.zip}
        </TextLine>
      </Paper>
    </Box>
  ));
};

function CustomerProfile() {
  const currentCustomer = useSessionStore.use.currentCustomer();
  const customerProfile = useCustomerStore.use.customerProfile();
  const getCustomerProfile = useCustomerStore.use.getCustomerProfile();

  useEffect(() => {
    getCustomerProfile().then();
  }, [currentCustomer, getCustomerProfile]);

  return (
    <>
      {!customerProfile && <Loader></Loader>}
      {customerProfile && (
        <>
          <PageHeader title="Customer Profile" heading="Customer Profile" />

          <Paper mt={4} px={6} pb={6}>
            <Stack spacing={1}>
              <MainHeading>{customerProfile.displayName}</MainHeading>
              <Heading>Billing Address</Heading>
              <BillingInfo customer={customerProfile} />
              <Heading>Service Locations</Heading>
              <ServiceLocations serviceLocations={customerProfile.serviceLocations} />
            </Stack>
          </Paper>
        </>
      )}
    </>
  );
}

export default CustomerProfile;
